import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PLACEHOLDER_PARTS } from '@/constants/marketplace.data'
import { MarketplaceBreadcrumb } from '@/components/common/marketplace-breadcrumb'
import { ImageGallery, PartCard } from '@/components/marketplace'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { SectionHeading } from '@/components/common'
import { ShieldCheck, ShoppingBag, Truck, RotateCcw, Award, CheckCircle2, Star, Plus, Minus, Check, Loader2 } from 'lucide-react'
import { appRoutes } from '@/constants'
import { NotFoundPage } from '@/pages/not-found-page'
import { usePartDetail } from '@/modules/parts/hooks/use-parts'
import { useAddToCart } from '@/modules/cart/hooks/use-cart'
import { useAuth } from '@/providers/auth-provider'
import { CartDrawer } from '@/modules/cart/components/cart-drawer'

export function PartDetailsPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { data: dbPart, isLoading } = usePartDetail(slug || '')
  const addToCart = useAddToCart()

  const [quantity, setQuantity] = useState(1)
  const [isAdded, setIsAdded] = useState(false)
  const [isCartOpen, setIsCartOpen] = useState(false)

  // Use live DB part if available, else placeholder fallback
  const fallback = PLACEHOLDER_PARTS.find((p) => p.slug === slug) || PLACEHOLDER_PARTS[0]
  const part = dbPart || fallback

  if (isLoading) {
    return (
      <div className="p-24 text-center flex flex-col items-center justify-center space-y-3">
        <Loader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading part specifications...</p>
      </div>
    )
  }

  if (!part) {
    return <NotFoundPage />
  }

  const relatedParts = PLACEHOLDER_PARTS.filter((p) => p.slug !== part.slug).slice(0, 2)

  // Formatted price
  const formattedPrice = part.priceText || (typeof part.price === 'number' ? `₹${part.price.toLocaleString('en-IN')}` : `₹${part.price}`)
  const unitPrice = typeof part.price === 'number' ? part.price : Number(String(part.price).replace(/[^0-9.]/g, '')) || 0
  const totalPrice = `₹${(unitPrice * quantity).toLocaleString('en-IN')}`

  // Images formatting
  let galleryImages: string[] = []
  if (Array.isArray(part.images) && part.images.length > 0) {
    galleryImages = part.images.map((img: any) => typeof img === 'string' ? img : img?.url || '')
  } else {
    galleryImages = ['https://images.unsplash.com/photo-1486006920555-c77dce18193b?auto=format&fit=crop&w=1000&q=80']
  }

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      navigate(appRoutes.auth.login)
      return
    }

    addToCart.mutate(
      { partId: Number(part.id) || 1, quantity },
      {
        onSuccess: () => {
          setIsAdded(true)
          setIsCartOpen(true)
          setTimeout(() => setIsAdded(false), 2000)
        },
      },
    )
  }

  const handleBuyNow = () => {
    if (!isAuthenticated) {
      navigate(appRoutes.auth.login)
      return
    }

    addToCart.mutate(
      { partId: Number(part.id) || 1, quantity },
      {
        onSuccess: () => {
          setIsCartOpen(true)
        },
      },
    )
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-12 py-6">
      <MarketplaceBreadcrumb
        items={[
          { label: 'Car Parts Catalog', href: appRoutes.parts },
          { label: part.title },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* Gallery & Spec Tabs */}
        <div className="lg:col-span-2 space-y-10">
          <ImageGallery images={part.images} title={part.title} />

          {/* Title & Pricing */}
          <div className="space-y-4 border-b border-border pb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-background text-foreground border border-border">
                    {part.category}
                  </Badge>
                  {part.isOEM && (
                    <Badge className="bg-primary/20 text-primary border border-primary/30">
                      <ShieldCheck className="h-3 w-3 mr-1" /> Genuine OEM Part
                    </Badge>
                  )}
                </div>
                <h1 className="font-display text-2xl sm:text-4xl font-extrabold text-foreground">{part.title}</h1>
                <p className="text-xs text-muted-foreground font-mono">Part Number: {part.partNumber}</p>
              </div>

              <div className="space-y-1 text-left sm:text-right">
                <span className="font-display text-3xl sm:text-4xl font-extrabold text-foreground">{part.priceText}</span>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">In Stock &amp; Ready To Ship</p>
              </div>
            </div>
          </div>

          {/* Guarantees & Shipping Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="p-4 rounded-xl border border-border bg-card shadow-xs space-y-2">
              <Truck className="h-5 w-5 text-primary" />
              <h4 className="font-display text-xs font-bold text-foreground uppercase tracking-wider">Fast Shipping</h4>
              <p className="text-xs text-muted-foreground">{part.shippingInfo}</p>
            </Card>

            <Card className="p-4 rounded-xl border border-border bg-card shadow-xs space-y-2">
              <Award className="h-5 w-5 text-primary" />
              <h4 className="font-display text-xs font-bold text-foreground uppercase tracking-wider">Warranty</h4>
              <p className="text-xs text-muted-foreground">{part.warranty}</p>
            </Card>

            <Card className="p-4 rounded-xl border border-border bg-card shadow-xs space-y-2">
              <RotateCcw className="h-5 w-5 text-primary" />
              <h4 className="font-display text-xs font-bold text-foreground uppercase tracking-wider">Returns</h4>
              <p className="text-xs text-muted-foreground">{part.returnPolicy}</p>
            </Card>
          </div>

          {/* Description & Technical Specifications */}
          <div className="space-y-4 border-t border-border pt-6">
            <h3 className="font-display text-xl font-bold text-foreground">Product Overview</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{part.description || 'Precision engineered automotive replacement part built to meet or exceed original equipment manufacturer standards.'}</p>

            {/* Vehicle Compatibility Matrix Summary */}
            {((part as any).vehicleBrand || (part as any).vehicleModel || (part.specifications as any)?.vehicleBrand) && (
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
                <span className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                  <CheckCircle2 className="size-4" /> Vehicle Fitment Confirmation
                </span>
                <p className="text-sm font-bold text-foreground">
                  Compatible with: {(part as any).vehicleBrand || (part.specifications as any)?.vehicleBrand} {(part as any).vehicleModel || (part.specifications as any)?.vehicleModel}
                </p>
                <p className="text-xs text-muted-foreground">
                  Applicable Years: {(part as any).vehicleYear || (part.specifications as any)?.vehicleYear || 'All Variants'} {((part as any).engineVariant || (part.specifications as any)?.engineVariant) ? `• ${(part as any).engineVariant || (part.specifications as any)?.engineVariant}` : ''}
                </p>
              </div>
            )}

            <h4 className="font-display text-base font-bold text-foreground pt-4">Technical Specifications</h4>
            <div className="rounded-xl border border-border bg-card divide-y divide-border shadow-xs">
              {Object.entries(part.specifications || {}).length > 0 ? (
                Object.entries(part.specifications || {}).map(([key, val]) => (
                  <div key={key} className="flex justify-between p-3.5 text-xs">
                    <span className="text-muted-foreground font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="text-foreground font-bold">{String(val)}</span>
                  </div>
                ))
              ) : (
                <>
                  <div className="flex justify-between p-3.5 text-xs">
                    <span className="text-muted-foreground font-medium">Manufacturer</span>
                    <span className="text-foreground font-bold">{part.brand}</span>
                  </div>
                  <div className="flex justify-between p-3.5 text-xs">
                    <span className="text-muted-foreground font-medium">OEM Part Number</span>
                    <span className="text-foreground font-bold font-mono">{part.partNumber}</span>
                  </div>
                  <div className="flex justify-between p-3.5 text-xs">
                    <span className="text-muted-foreground font-medium">Condition</span>
                    <span className="text-foreground font-bold">{part.condition || 'Brand New'}</span>
                  </div>
                  <div className="flex justify-between p-3.5 text-xs">
                    <span className="text-muted-foreground font-medium">Category</span>
                    <span className="text-foreground font-bold">{part.category}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Compatibility */}
          <div className="space-y-3 border-t border-border pt-6">
            <h3 className="font-display text-xl font-bold text-foreground">Verified Compatible Models</h3>
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(part.compatibility) ? part.compatibility : [part.compatibility || 'Universal Fit']).map((c, i) => (
                <Badge key={i} className="bg-card border border-border text-xs text-foreground p-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 mr-1.5" /> {String(c)}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Action Panel Sidebar */}
        <div className="space-y-6 lg:sticky lg:top-24">
          <Card className="p-6 rounded-2xl border border-border bg-card space-y-6 shadow-md">
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Total Price</span>
              <div className="font-display-semibold text-3xl font-extrabold text-foreground">{totalPrice}</div>
              {quantity > 1 && (
                <p className="text-xs text-muted-foreground font-mono">({formattedPrice} each)</p>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground uppercase tracking-wider">Quantity</label>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-9 rounded-lg"
                  disabled={quantity <= 1}
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                >
                  <Minus className="size-4" />
                </Button>
                <span className="w-12 text-center font-display font-bold text-lg text-foreground">{quantity}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-9 rounded-lg"
                  onClick={() => setQuantity((q) => q + 1)}
                >
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full bg-primary text-white hover:bg-primary/90 glow-md font-bold"
                onClick={handleAddToCart}
                disabled={addToCart.isPending}
              >
                {addToCart.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : isAdded ? (
                  <Check className="mr-2 h-4 w-4 text-emerald-400" />
                ) : (
                  <ShoppingBag className="mr-2 h-4 w-4" />
                )}
                {isAdded ? 'Added To Cart!' : 'Add To Cart'}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full border-border text-foreground hover:bg-accent font-bold"
                onClick={handleBuyNow}
                disabled={addToCart.isPending}
              >
                Buy Now
              </Button>
            </div>

            <div className="pt-4 border-t border-border space-y-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <span>Buyer Protection Guarantee</span>
              </div>
            </div>
          </Card>

          {/* Seller Summary */}
          <Card className="p-6 rounded-2xl border border-border bg-card space-y-3 shadow-sm">
            <h4 className="font-display text-sm font-bold text-foreground">Sold &amp; Fulfilled By</h4>
            <p className="text-xs font-bold text-primary">{part.seller?.name || 'Exotic Autocart Certified Partner'}</p>
            <p className="text-xs text-muted-foreground">{part.seller?.type || 'OEM Distributor'} • {part.seller?.location || 'Direct Dispatch'}</p>
            <div className="flex items-center gap-1 text-xs text-amber-500 pt-1">
              <Star className="h-3.5 w-3.5 fill-amber-400" />
              <span>{part.seller?.rating || '4.9'} / 5.0 Seller Rating</span>
            </div>
          </Card>
        </div>
      </div>

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      {/* Related Parts */}
      <div className="border-t border-border pt-12 space-y-8">
        <SectionHeading
          eyebrow="Related Components"
          title="Frequently Bought Together"
          description="Explore compatible performance parts and OEM assemblies."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {relatedParts.map((p) => (
            <PartCard key={p.id} part={p} />
          ))}
        </div>
      </div>
    </div>
  )
}
